// Firestore utility functions for admin data
import { db, isFirebaseConfigured } from '../firebase';
import { resolveBookingDeliveryStatus } from './deliveryStatus';
import { normalizeSocialMediaShare } from './socialMediaShareStatus';
import { getBookingShortKey } from './bookingShortId';
import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  setDoc, 
  deleteDoc, 
  query, 
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';

// Collections
const BOOKINGS_COLLECTION = 'bookings';
/** Public read doc for home ticker (no PII). Requires Firestore rule: siteMeta/bookedDatesTicker allow read: if true; */
const SITE_META_COLLECTION = 'siteMeta';
const BOOKED_DATES_TICKER_DOC = 'bookedDatesTicker';
const TEAM_MEMBERS_COLLECTION = 'teamMembers';

/**
 * Removes undefined values from an object (Firestore doesn't allow undefined)
 */
const generateClientTrackingToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tr_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
};

const removeUndefined = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item));
  }
  
  const cleaned = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = removeUndefined(obj[key]);
    }
  }
  return cleaned;
};

/**
 * Ensures all booking fields are present with default values
 * This function standardizes booking data structure for consistency
 */
export const ensureBookingFields = (booking) => {
  const defaultAssignedTeam = {
    photographers: [],
    cinematographers: [],
    drone: [],
    photo_editors: [],
    video_editors: []
  };

  // Calculate remainingAmount if not present
  const packagePrice = parseFloat(booking.packagePrice) || 0;
  const discountAmount = parseFloat(booking.discountAmount) || 0;
  const paidAmount = parseFloat(booking.paidAmount) || 0;
  const calculatedRemaining = Math.max(0, packagePrice - discountAmount - paidAmount);

  const shortKey = booking.id ? getBookingShortKey(booking.id) : '';

  const ensured = {
    ...booking,
    // Lookup key for track-deliverables (KH-XXXXXX → lowercase 6-char tail)
    ...(shortKey ? { bookingShortKey: shortKey } : {}),
    // Payment fields
    discountAmount: booking.discountAmount !== undefined ? booking.discountAmount : 0,
    paidAmount: booking.paidAmount !== undefined ? booking.paidAmount : 0,
    remainingAmount: booking.remainingAmount !== undefined ? booking.remainingAmount : calculatedRemaining,
    transactionId: booking.transactionId !== undefined && booking.transactionId !== 'N/A' ? booking.transactionId : '',
    paymentStatus: booking.paymentStatus || 'Not PAID',
    
    // Team assignment fields - always ensure it's an object, never null or undefined
    assignedTeam: (booking.assignedTeam && typeof booking.assignedTeam === 'object') ? {
      photographers: Array.isArray(booking.assignedTeam.photographers) ? booking.assignedTeam.photographers : [],
      cinematographers: Array.isArray(booking.assignedTeam.cinematographers) ? booking.assignedTeam.cinematographers : [],
      drone: Array.isArray(booking.assignedTeam.drone) ? booking.assignedTeam.drone : [],
      photo_editors: Array.isArray(booking.assignedTeam.photo_editors) ? booking.assignedTeam.photo_editors : [],
      video_editors: Array.isArray(booking.assignedTeam.video_editors) ? booking.assignedTeam.video_editors : []
    } : defaultAssignedTeam,
    
    // Event type
    eventType: booking.eventType || 'single-day',

    // Delivery status (canonical slugs; legacy labels normalized)
    photoDeliveryStatus: resolveBookingDeliveryStatus(booking, booking.photoDeliveryStatus),
    videoDeliveryStatus: resolveBookingDeliveryStatus(booking, booking.videoDeliveryStatus),
    socialMediaShare: normalizeSocialMediaShare(booking.socialMediaShare),
    // Opaque token for client deliverables page (written on first save if missing)
    ...(booking.clientTrackingToken ? { clientTrackingToken: booking.clientTrackingToken } : {})
  };
  
  // Only include dayLocations if it exists and is an array (for multi-day events)
  if (booking.eventType === 'multi-day' && Array.isArray(booking.dayLocations) && booking.dayLocations.length > 0) {
    ensured.dayLocations = booking.dayLocations.map(day => {
      const cleanedDay = {
        ...day,
        assignedTeam: day.assignedTeam ? {
          photographers: Array.isArray(day.assignedTeam.photographers) ? day.assignedTeam.photographers : [],
          cinematographers: Array.isArray(day.assignedTeam.cinematographers) ? day.assignedTeam.cinematographers : [],
          drone: Array.isArray(day.assignedTeam.drone) ? day.assignedTeam.drone : [],
          photo_editors: Array.isArray(day.assignedTeam.photo_editors) ? day.assignedTeam.photo_editors : [],
          video_editors: Array.isArray(day.assignedTeam.video_editors) ? day.assignedTeam.video_editors : []
        } : defaultAssignedTeam
      };
      // Remove undefined values from day object
      return removeUndefined(cleanedDay);
    });
  }
  
  // Remove all undefined values before returning (Firestore doesn't allow undefined)
  return removeUndefined(ensured);
};

/**
 * Migrates all existing bookings in Firestore to include missing fields
 * This should be run once to update all existing bookings
 * @returns {Promise<{success: boolean, updated: number, message: string}>}
 */
export const migrateExistingBookings = async () => {
  if (!isFirebaseConfigured || !db) {
    return {
      success: false,
      updated: 0,
      message: 'Firebase is not configured'
    };
  }

  try {
    const bookingsRef = collection(db, BOOKINGS_COLLECTION);
    const querySnapshot = await getDocs(bookingsRef);
    
    const bookingsToUpdate = [];
    querySnapshot.forEach((doc) => {
      const bookingData = { id: doc.id, ...doc.data() };
      const ensuredBooking = ensureBookingFields(bookingData);
      let finalBooking = ensuredBooking;
      if (!finalBooking.clientTrackingToken) {
        finalBooking = { ...finalBooking, clientTrackingToken: generateClientTrackingToken() };
      }

      const deliveryCanonicalMismatch =
        (bookingData.photoDeliveryStatus ?? '') !== ensuredBooking.photoDeliveryStatus ||
        (bookingData.videoDeliveryStatus ?? '') !== ensuredBooking.videoDeliveryStatus;

      // Check if booking needs update (has missing fields or incorrect values)
      const desiredShortKey = bookingData.id ? getBookingShortKey(bookingData.id) : '';
      const needsShortKeyFix = Boolean(desiredShortKey && bookingData.bookingShortKey !== desiredShortKey);

      const needsUpdate =
        bookingData.discountAmount === undefined ||
        bookingData.assignedTeam === undefined ||
        bookingData.transactionId === undefined ||
        bookingData.transactionId === 'N/A' ||
        !bookingData.assignedTeam ||
        (bookingData.assignedTeam &&
          (bookingData.assignedTeam.photo_editors === undefined ||
            bookingData.assignedTeam.video_editors === undefined ||
            bookingData.assignedTeam.photographers === undefined ||
            bookingData.assignedTeam.cinematographers === undefined ||
            bookingData.assignedTeam.drone === undefined)) ||
        !bookingData.clientTrackingToken ||
        deliveryCanonicalMismatch ||
        needsShortKeyFix;

      if (needsUpdate) {
        bookingsToUpdate.push(finalBooking);
      }
    });

    if (bookingsToUpdate.length === 0) {
      return {
        success: true,
        updated: 0,
        message: 'All bookings are already up to date'
      };
    }

    // Update all bookings that need migration
    // Save the full booking object to ensure all fields are properly set
    const updatePromises = bookingsToUpdate.map(booking => {
      const bookingRef = doc(db, BOOKINGS_COLLECTION, booking.id);
      // Save the entire ensured booking object to Firestore
      // This ensures all fields (including missing ones) are added
      return setDoc(bookingRef, booking, { merge: true });
    });

    await Promise.all(updatePromises);

    // Also update localStorage
    const allBookings = querySnapshot.docs.map(doc => {
      const bookingData = { id: doc.id, ...doc.data() };
      return ensureBookingFields(bookingData);
    });
    saveBookingsToLocalStorage(allBookings);

    return {
      success: true,
      updated: bookingsToUpdate.length,
      message: `Successfully migrated ${bookingsToUpdate.length} booking(s)`
    };
  } catch (error) {
    console.error('Error migrating bookings:', error);
    return {
      success: false,
      updated: 0,
      message: `Migration failed: ${error.message}`
    };
  }
};

// ========== BOOKINGS ==========

async function fetchBookingsFromFirestoreOnce() {
  const bookingsRef = collection(db, BOOKINGS_COLLECTION);
  const q = query(bookingsRef, orderBy('eventDate', 'desc'));
  const querySnapshot = await getDocs(q);
  const bookings = [];
  querySnapshot.forEach((docSnap) => {
    const bookingData = { id: docSnap.id, ...docSnap.data() };
    bookings.push(ensureBookingFields(bookingData));
  });
  return bookings;
}

/**
 * True when Firestore returned bookings; false on permission / network error (no localStorage fallback).
 * Used by public UI that must not treat denied reads as “no bookings”.
 */
export const tryLoadBookingsFromFirestore = async () => {
  if (!isFirebaseConfigured || !db) {
    return { ok: false, bookings: [] };
  }
  try {
    const bookings = await fetchBookingsFromFirestoreOnce();
    return { ok: true, bookings };
  } catch (error) {
    console.error('Error loading bookings from Firestore:', error);
    return { ok: false, bookings: [] };
  }
};

/** Upcoming event dates as YYYY-MM-DD (no PII). */
export const extractUpcomingEventIsoDateStrings = (bookings) => {
  const dateSet = new Set();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  bookings.forEach((booking) => {
    if (booking.eventType === 'multi-day' && Array.isArray(booking.dayLocations)) {
      booking.dayLocations.forEach((day) => {
        if (day.date) dateSet.add(day.date);
      });
    } else if (booking.eventDate) {
      dateSet.add(booking.eventDate);
    }
  });

  return Array.from(dateSet)
    .map((d) => {
      const parsed = new Date(d);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    })
    .filter(Boolean)
    .filter((d) => d >= today)
    .sort((a, b) => a - b)
    .map((d) => d.toISOString().slice(0, 10));
};

/**
 * Sync public ticker dates (admin client only). Visitors read this doc without booking PII.
 * Firestore rules must allow public read on siteMeta/bookedDatesTicker and admin-only write.
 */
export const syncPublicBookedDatesTicker = async (bookings) => {
  if (!isFirebaseConfigured || !db) return;
  const isoDates = extractUpcomingEventIsoDateStrings(bookings);
  try {
    const ref = doc(db, SITE_META_COLLECTION, BOOKED_DATES_TICKER_DOC);
    await setDoc(
      ref,
      { dates: isoDates, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (error) {
    console.error('syncPublicBookedDatesTicker:', error);
  }
};

/**
 * @returns {{ dates: string[] } | null} null if unreadable or doc missing
 */
export const loadPublicBookedDatesTicker = async () => {
  if (!isFirebaseConfigured || !db) return null;
  try {
    const ref = doc(db, SITE_META_COLLECTION, BOOKED_DATES_TICKER_DOC);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    const dates = Array.isArray(data.dates) ? data.dates : [];
    return { dates };
  } catch (error) {
    console.error('loadPublicBookedDatesTicker:', error);
    return null;
  }
};

export const loadBookings = async () => {
  if (isFirebaseConfigured && db) {
    try {
      return await fetchBookingsFromFirestoreOnce();
    } catch (error) {
      console.error('Error loading bookings from Firestore:', error);
      // Fallback to localStorage
      const localBookings = loadBookingsFromLocalStorage();
      return localBookings.map(booking => ensureBookingFields(booking));
    }
  } else {
    const localBookings = loadBookingsFromLocalStorage();
    return localBookings.map(booking => ensureBookingFields(booking));
  }
};

/** Ensures fields + client tracking token (for save or building client URLs). */
export const prepareBookingForPersistence = (booking) => {
  let cleanedBooking = ensureBookingFields(booking);
  if (!cleanedBooking.clientTrackingToken) {
    cleanedBooking = { ...cleanedBooking, clientTrackingToken: generateClientTrackingToken() };
  }
  return cleanedBooking;
};

export const saveBooking = async (booking) => {
  const cleanedBooking = prepareBookingForPersistence(booking);
  
  if (isFirebaseConfigured && db) {
    try {
      const bookingRef = doc(db, BOOKINGS_COLLECTION, cleanedBooking.id);
      await setDoc(bookingRef, cleanedBooking, { merge: true });
      // Also save to localStorage as backup
      saveBookingsToLocalStorage([cleanedBooking]);
      return true;
    } catch (error) {
      console.error('Error saving booking to Firestore:', error);
      // Fallback to localStorage
      return saveBookingsToLocalStorage([cleanedBooking]);
    }
  } else {
    return saveBookingsToLocalStorage([cleanedBooking]);
  }
};

export const saveAllBookings = async (bookings) => {
  const cleanedBookings = bookings.map((booking) => prepareBookingForPersistence(booking));
  
  if (isFirebaseConfigured && db) {
    try {
      // Save all bookings to Firestore
      const promises = cleanedBookings.map(booking => {
        const bookingRef = doc(db, BOOKINGS_COLLECTION, booking.id);
        return setDoc(bookingRef, booking, { merge: true });
      });
      await Promise.all(promises);
      // Also save to localStorage as backup
      saveBookingsToLocalStorage(cleanedBookings);
      return true;
    } catch (error) {
      console.error('Error saving bookings to Firestore:', error);
      // Fallback to localStorage
      return saveBookingsToLocalStorage(cleanedBookings);
    }
  } else {
    return saveBookingsToLocalStorage(cleanedBookings);
  }
};

export const deleteBooking = async (bookingId) => {
  if (isFirebaseConfigured && db) {
    try {
      const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
      await deleteDoc(bookingRef);
      // Also update localStorage
      const bookings = loadBookingsFromLocalStorage();
      const updatedBookings = bookings.filter(b => b.id !== bookingId);
      saveBookingsToLocalStorage(updatedBookings);
      return true;
    } catch (error) {
      console.error('Error deleting booking from Firestore:', error);
      // Fallback to localStorage
      const bookings = loadBookingsFromLocalStorage();
      const updatedBookings = bookings.filter(b => b.id !== bookingId);
      return saveBookingsToLocalStorage(updatedBookings);
    }
  } else {
    const bookings = loadBookingsFromLocalStorage();
    const updatedBookings = bookings.filter(b => b.id !== bookingId);
    return saveBookingsToLocalStorage(updatedBookings);
  }
};

// Real-time listener for bookings
export const subscribeToBookings = (callback) => {
  if (isFirebaseConfigured && db) {
    const bookingsRef = collection(db, BOOKINGS_COLLECTION);
    const q = query(bookingsRef, orderBy('eventDate', 'desc'));
    return onSnapshot(q, (querySnapshot) => {
      const bookings = [];
      querySnapshot.forEach((doc) => {
        const bookingData = { id: doc.id, ...doc.data() };
        // Ensure all fields are present when loading
        bookings.push(ensureBookingFields(bookingData));
      });
      callback(bookings);
    }, (error) => {
      console.error('Error in bookings subscription:', error);
      // Fallback to localStorage
      const localBookings = loadBookingsFromLocalStorage();
      callback(localBookings.map(booking => ensureBookingFields(booking)));
    });
  } else {
    // Return localStorage data
    const localBookings = loadBookingsFromLocalStorage();
    callback(localBookings.map(booking => ensureBookingFields(booking)));
    return () => {}; // Return empty unsubscribe function
  }
};

// ========== TEAM MEMBERS ==========

export const loadTeamMembers = async () => {
  if (isFirebaseConfigured && db) {
    try {
      const teamRef = collection(db, TEAM_MEMBERS_COLLECTION);
      const querySnapshot = await getDocs(teamRef);
      const members = [];
      querySnapshot.forEach((doc) => {
        members.push({ id: doc.id, ...doc.data() });
      });
      return members;
    } catch (error) {
      console.error('Error loading team members from Firestore:', error);
      // Fallback to localStorage
      return loadTeamMembersFromLocalStorage();
    }
  } else {
    return loadTeamMembersFromLocalStorage();
  }
};

export const saveTeamMembers = async (members) => {
  if (isFirebaseConfigured && db) {
    try {
      // Delete all existing members first
      const teamRef = collection(db, TEAM_MEMBERS_COLLECTION);
      const querySnapshot = await getDocs(teamRef);
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Add all new members
      const addPromises = members.map(member => {
        const memberRef = doc(db, TEAM_MEMBERS_COLLECTION, member.id);
        return setDoc(memberRef, member);
      });
      await Promise.all(addPromises);
      
      // Also save to localStorage as backup
      saveTeamMembersToLocalStorage(members);
      return true;
    } catch (error) {
      console.error('Error saving team members to Firestore:', error);
      // Fallback to localStorage
      return saveTeamMembersToLocalStorage(members);
    }
  } else {
    return saveTeamMembersToLocalStorage(members);
  }
};

// ========== LOCALSTORAGE FALLBACK ==========

const loadBookingsFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('adminBookings');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((booking, index) => ({
        ...booking,
        id: booking.id || `booking_${index}_${Date.now()}`
      }));
    }
    return [];
  } catch (error) {
    console.error('Error loading bookings from localStorage:', error);
    return [];
  }
};

const saveBookingsToLocalStorage = (bookings) => {
  try {
    localStorage.setItem('adminBookings', JSON.stringify(bookings));
    return true;
  } catch (error) {
    console.error('Error saving bookings to localStorage:', error);
    return false;
  }
};

const loadTeamMembersFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('teamMembers');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading team members from localStorage:', error);
    return [];
  }
};

const saveTeamMembersToLocalStorage = (members) => {
  try {
    localStorage.setItem('teamMembers', JSON.stringify(members));
    return true;
  } catch (error) {
    console.error('Error saving team members to localStorage:', error);
    return false;
  }
};
